import React, { useState, useEffect } from 'react';
import axiosInstance from '../apis/axiosConfig';
import { toast } from 'react-toastify';
import { FaBuilding, FaSearch, FaTrash, FaMapMarkerAlt, FaEye, FaEdit, FaShareAlt } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import '../pages_premium.css';
import './PropertiesList.css';
import { getFullImageUrl, getFrontendBaseUrl } from '../utils/url';
import AdminShareModal from '../components/common/AdminShareModal';
import AdminPagination from '../components/common/AdminPagination';

const safeParseJson = (val) => {
    if (!val) return null;
    let curr = val;
    while (typeof curr === 'string') {
        try {
            const parsed = JSON.parse(curr);
            if (parsed === curr) break;
            curr = parsed;
        } catch (e) {
            break;
        }
    }
    return curr;
};

const ExpandableText = ({ text, maxLength = 50 }) => {
    const [expanded, setExpanded] = useState(false);
    if (!text) return <span className="text-muted-na">N/A</span>;
    
    let content = '';
    const formatValue = (key, val) => {
        const formattedKey = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
        const formattedVal = val === true ? 'Yes' : val === false ? 'No' : val;
        return `${formattedKey}: ${formattedVal}`;
    };

    const parsed = safeParseJson(text);

    if (parsed && typeof parsed === 'object') {
        if (Array.isArray(parsed)) {
            content = parsed.map(s => String(s)).join(', ');
        } else {
            content = Object.entries(parsed)
                .map(([k, v]) => formatValue(k, v))
                .join(', ');
        }
    } else {
        content = String(parsed || text);
    }

    if (!content) return <span className="text-muted-na">N/A</span>;

    if (content.length <= maxLength) return <span className="expandable-content">{content}</span>;
    
    return (
        <div className="expandable-text-wrap">
            <span className="expandable-content">
                {expanded ? content : `${content.substring(0, maxLength)}...`}
            </span>
            <button 
                type="button"
                onClick={() => setExpanded(!expanded)} 
                className="expand-toggle-btn"
            >
                {expanded ? 'Show Less' : 'Show More'}
            </button>
        </div>
    );
};

function PropertiesList() {
    const [properties, setProperties] = useState([]);
    const [search, setSearch] = useState('');
    const [listerFilter, setListerFilter] = useState('all');
    const [shareItem, setShareItem] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const location = useLocation();
    const navigate = useNavigate();
    
    const queryParams = new URLSearchParams(location.search);
    const roleParam = queryParams.get('role');

    const handleShareProperty = (property) => {
        const frontendUrl = getFrontendBaseUrl();
        const isFlatmate = property.offerType === 'Flatmate';
        const isLease = property.offerType === 'Lease Transfer';
        let path = `/rooms/${property.id}`;
        if (isFlatmate) path = `/flatmates/${property.id}`;
        else if (isLease) path = `/lease-transfer/${property.id}`;

        const firstImg = property.PropertyImages && property.PropertyImages[0] 
            ? (property.PropertyImages[0].image_url || property.PropertyImages[0]) 
            : '';

        setShareItem({
            url: `${frontendUrl}${path}`,
            title: property.title || 'Property Listing',
            category: property.offerType || 'Room',
            price: property.price ? `$${property.price}/wk` : '',
            location: `${property.city || ''}${property.state ? `, ${property.state}` : ''}` || property.address || '',
            image: firstImg,
            description: property.description || ''
        });
    };

    useEffect(() => {
        if (roleParam) {
            setListerFilter(roleParam);
        }
        fetchProperties();
    }, [roleParam]);

    const fetchProperties = async () => {
        try {
            const response = await axiosInstance.get('/properties');
            setProperties(response.data || []);
        } catch (error) {
            console.error('Error fetching properties:', error);
            toast.error('Failed to load properties');
        }
    };

    const deleteProperty = async (id) => {
        if (!window.confirm('Are you sure you want to delete this property?')) return;
        try {
            await axiosInstance.delete(`/properties/${id}`);
            toast.success('Property deleted successfully');
            fetchProperties();
        } catch (error) {
            console.error('Error deleting property:', error);
            toast.error('Failed to delete property');
        }
    };

    const getListerType = (property) => {
        const role = String(property.listing_role || property.User?.role || '').toLowerCase();
        if (role === 'student') return 'Student';
        if (role === 'agent' || role === 'lister') return 'Lister';
        if (role === 'owner' || role === 'business_owner' || role === 'business') return 'Business Owner';
        if (role === 'admin') return 'Admin';
        return role ? role.charAt(0).toUpperCase() + role.slice(1) : 'Lister';
    };

    const getListerBadgeClass = (property) => {
        const role = String(property.listing_role || property.User?.role || '').toLowerCase();
        if (role === 'student') return 'lister-badge lister-badge-student';
        if (role === 'agent' || role === 'lister') return 'lister-badge lister-badge-lister';
        if (role === 'owner' || role === 'business_owner' || role === 'business') return 'lister-badge lister-badge-owner';
        if (role === 'admin') return 'lister-badge lister-badge-admin';
        return 'lister-badge lister-badge-default';
    };

    const filtered = properties.filter(p => {
        const listerType = getListerType(p);
        const matchesSearch = p.title?.toLowerCase().includes(search.toLowerCase()) ||
            p.address?.toLowerCase().includes(search.toLowerCase()) ||
            p.offerType?.toLowerCase().includes(search.toLowerCase()) ||
            listerType.toLowerCase().includes(search.toLowerCase()) ||
            p.User?.name?.toLowerCase().includes(search.toLowerCase());

        if (!matchesSearch) return false;

        if (listerFilter !== 'all') {
            if (listerFilter === 'student' && listerType !== 'Student') return false;
            if (listerFilter === 'agent' && listerType !== 'Lister') return false;
            if (listerFilter === 'owner' && listerType !== 'Business Owner') return false;
            if (listerFilter === 'admin' && listerType !== 'Admin') return false;
        }

        return true;
    });

    useEffect(() => {
        setCurrentPage(1);
    }, [search, listerFilter]);

    const paginatedProperties = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    const formatPrice = (price) => {
        if (!price) return 'N/A';
        return '₹' + Number(price).toLocaleString('en-IN');
    };

    const formatDate = (val) => {
        if (!val) return '-';
        const dateObj = new Date(val);
        if (!isNaN(dateObj.getTime())) {
            const day = String(dateObj.getDate()).padStart(2, '0');
            const month = String(dateObj.getMonth() + 1).padStart(2, '0');
            const year = dateObj.getFullYear();
            return `${day}/${month}/${year}`;
        }
        return String(val);
    };

    const getPageTitle = () => {
        if (listerFilter === 'student') return 'Student Properties';
        if (listerFilter === 'owner') return 'Business Owner Properties';
        if (listerFilter === 'agent') return 'Lister Properties';
        if (listerFilter === 'admin') return 'Admin Properties';
        return 'All Properties';
    };

    return (
        <div className="page-premium-wrapper properties-list-page">
            <div className="page-premium-content">

                {/* Hero Header */}
                <div className="page-hero-header page-hero-properties">
                    <div className="ph-blob ph-blob-1"></div>
                    <div className="ph-blob ph-blob-2"></div>

                    <div className="ph-left">
                        <div className="ph-icon">
                            <FaBuilding size={26} />
                        </div>
                        <div>
                            <h1 className="ph-title">{getPageTitle()}</h1>
                            <p className="ph-subtitle">View, manage and delete listed properties</p>
                        </div>
                    </div>

                    <div className="ph-right">
                        <div className="ph-count-badge">
                            <span className="count-num">{properties.length}</span>
                            <span className="count-lbl">Total Properties</span>
                        </div>
                    </div>
                </div>

                {/* Table Card */}
                <div className="premium-table-card">
                    <div className="ptable-toolbar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                        <div className="ptable-title">
                            <span className="ptable-title-dot"></span>
                            {getPageTitle()} ({filtered.length})
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                            <select
                                className="form-control"
                                style={{ width: '190px', height: '40px', fontSize: '0.84rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontWeight: 500 }}
                                value={listerFilter}
                                onChange={(e) => setListerFilter(e.target.value)}
                            >
                                <option value="all">All Lister Types</option>
                                <option value="student">🎓 Student</option>
                                <option value="agent">🏢 Lister</option>
                                <option value="owner">💼 Business Owner</option>
                                <option value="admin">🛡️ Admin</option>
                            </select>
                            <div className="ptable-search">
                                <FaSearch className="ptable-search-icon" />
                                <input
                                    type="text"
                                    placeholder="Search by title, location, lister..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="ptable-wrap">
                        <table className="premium-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th style={{ minWidth: '240px' }}>Title</th>
                                    <th style={{ whiteSpace: 'nowrap' }}>Lister Type</th>
                                    <th>Offer Type</th>
                                    <th>Location</th>
                                    <th>Rent</th>
                                    <th style={{ minWidth: '160px' }}>About Home</th>
                                    <th style={{ minWidth: '160px' }}>About Room</th>
                                    <th style={{ minWidth: '160px' }}>Rent & Availability</th>
                                    <th>Available Date</th>
                                    <th>Inspection Date</th>
                                    <th>Min Stay</th>
                                    <th>Max Stay</th>
                                    <th style={{ textAlign: 'center' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedProperties.map((property, idx) => (
                                    <tr key={property.id}>
                                        <td className="prop-index">{(currentPage - 1) * pageSize + idx + 1}</td>
                                        <td style={{ minWidth: '240px' }}>
                                            <div className="prop-title-cell">
                                                {property.PropertyImages && property.PropertyImages.length > 0 ? (
                                                    <img 
                                                        src={getFullImageUrl(property.PropertyImages[0].image_url)} 
                                                        alt="Property Thumbnail" 
                                                        className="prop-thumb"
                                                    />
                                                ) : (
                                                    <div className="prop-thumb-fallback">
                                                        <FaBuilding size={14} />
                                                    </div>
                                                )}
                                                <strong className="prop-title-text">{property.title || 'Untitled'}</strong>
                                            </div>
                                        </td>
                                        <td style={{ whiteSpace: 'nowrap' }}>
                                            <span className={getListerBadgeClass(property)}>
                                                {getListerType(property)}
                                            </span>
                                            {property.User?.name && (
                                                <div style={{ fontSize: '0.74rem', color: '#64748b', marginTop: '3px', fontWeight: 500 }}>
                                                    {property.User.name}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <span className="offer-tag">
                                                {property.offerType || 'N/A'}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="location-cell" title={property.address || 'N/A'}>
                                                <FaMapMarkerAlt className="location-icon" />
                                                <span>{property.address || 'N/A'}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="price-badge">{formatPrice(property.rent)}</span>
                                        </td>
                                        <td><ExpandableText text={property.about_home} maxLength={40} /></td>
                                        <td><ExpandableText text={property.about_room} maxLength={40} /></td>
                                        <td><ExpandableText text={property.rent_and_availability} maxLength={40} /></td>
                                        <td>{property.availableDate || 'N/A'}</td>
                                        <td>{formatDate(property.inspection_date || property.inspectionDate)}</td>
                                        <td>{property.minStay || 'N/A'}</td>
                                        <td>{property.maxStay || 'N/A'}</td>
                                        <td>
                                            <div className="prop-actions">
                                                <button
                                                    className="action-btn action-btn-share"
                                                    data-tooltip="Share Listing"
                                                    title="Share Listing"
                                                    onClick={() => handleShareProperty(property)}
                                                >
                                                    <FaShareAlt />
                                                </button>
                                                <button
                                                    className="action-btn action-btn-view"
                                                    data-tooltip="View Property"
                                                    onClick={() => navigate(`/properties/${property.id}`)}
                                                >
                                                    <FaEye />
                                                </button>
                                                <button
                                                    className="action-btn action-btn-edit"
                                                    data-tooltip="Edit Property"
                                                    onClick={() => navigate(`/properties/edit/${property.id}`)}
                                                >
                                                    <FaEdit />
                                                </button>
                                                <button
                                                    className="action-btn action-btn-delete"
                                                    data-tooltip="Delete Property"
                                                    onClick={() => deleteProperty(property.id)}
                                                >
                                                    <FaTrash />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {filtered.length === 0 && (
                                    <tr>
                                        <td colSpan="14">
                                            <div className="ptable-empty">
                                                <div className="ptable-empty-icon">🏠</div>
                                                <p>No properties found.</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <AdminPagination
                        totalItems={filtered.length}
                        currentPage={currentPage}
                        pageSize={pageSize}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={(newSize) => { setPageSize(newSize); setCurrentPage(1); }}
                        itemLabel="properties"
                    />
                </div>

            </div>

            <AdminShareModal
                isOpen={!!shareItem}
                onClose={() => setShareItem(null)}
                {...shareItem}
            />
        </div>
    );
}

export default PropertiesList;
